<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import MessageBoxService from "../../../../services/MessageBox.ts";
import AddListItemForm from "./AddListItemForm.vue";
import { Variant, VariantList } from "../../../../types/models.ts";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  id: props.data?.id || "",
  name: props.data?.name || props.listCreateName || "",
  active: props.data?.active || true,
  list: props.data?.list || [],
  uid: props.data?.uid || "",
});

//setFieldRules('type', [required('type is required')])
setFieldRules("name", [required("Name is required")]);

const handleListAddedWhileCreating = (data: VariantList) => {
  formData.value.list.push(data);
  variantList.value.push(data);
};

const handleListAdded = (data: VariantList) => {
  formData.value.list.push(data);
  //variantList.value.push(data);
  emit("save", formData.value);
};

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const variantList = ref<VariantList[]>([]);

const handleShowNewListForm = () => {
  addListMode.value = "create";
  selectedVariant.value = undefined;
  showAddListForm.value = true;
};
onMounted(() => {
  variantList.value = props.data?.list || [];
});

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
      variantList.value = [];
    } else if (props.data) {
      setFormData(props.data);
      variantList.value = props.data.list;
    }
})


//const selectedList = ref(any)

const toggleStatus = async (data: any, index: number) => {
  //selectedList.value = data
  const confirmed = await MessageBoxService.confirm({
    title: "Change Status",
    message:
      "Are you sure you want to " +
      (data.active == 1 ? "Disabled" : "Enable") +
      " the record?",
    confirmText: data.active == 1 ? "Disabled" : "Enable",
    cancelText: "Cancel",
    type: "warning",
  });

  if (confirmed) {
    handleListStatus(data, index);
  }
};
const handleListStatus = async (data: Variant, _index: number) => {
  const apiData = {
    id: data.uid,
    active: data.active == 1 ? 0 : 1,
  };
  const response = await api.post(props.endPoint + "list-status", apiData);
  if (response.success == true) {
    emit("save", response.data);
    data.active = data.active == 1 ? false : true;
  }
};

const showAddListForm = ref(false);
const selectedVariant = ref<Variant>();
const addListMode = ref<
  "showQuickView" | "create" | "edit" | "view" | "toggleStatus"
>("view");
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-2 gap-6">
        <TextInput
          v-model="fields.name.value"
          label="Name"
          placeholder="Enter Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
      <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg" v-if="mode != 'create'">
        <div class="">
          <span class="text-2xl font-medium text-gray-900 dark:text-white"
            >Variants</span
          >
          <Buttons
            id="form-add-denomination"
            class="float-right h-[35px] ml-2"
            @click="handleShowNewListForm"
            caption="Add new Variant"
            icon="FilePlus"
          />
        </div>
        <div class="mt-4">
          <table
            class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
            id="table-records-variant-list"
          >
            <thead class="bg-gray-50 dark:bg-gray-800">
              <tr id="table-headers">
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div class="flex items-center gap-1">Name</div>
                </th>
                <th
                  class="w-[100px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 sticky right-0 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div class="flex items-center gap-1">Active</div>
                </th>
                <th
                  class="w-[100px] px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-800 shadow-l"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
            >
              <tr v-for="(item, index) in variantList" :key="item.name">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div v-html="item.name"></div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="{
                      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200':
                        item.active == true,
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200':
                        item.active != true,
                    }"
                  >
                    {{ item.active }}
                  </span>
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-right space-x-2 sticky right-0 bg-white dark:bg-gray-900 shadow-l"
                  id="table-actions"
                >
                  <button
                    key="disable-records"
                    id="table-action-toggle-status"
                    @click="toggleStatus(item, index)"
                    :class="[
                      'text-danger-600 hover:text-danger-700',
                      'dark:text-danger-400 dark:hover:text-danger-300',
                    ]"
                    :title="item.active == true ? 'Disable' : 'Enable'"
                  >
                    <IconComponent
                      :name="item.active == true ? 'Ban' : 'CircleCheck'"
                    />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </ModalFormTemplate>
  <AddListItemForm
    title="Variant List"
    :data="selectedVariant"
    :variant="formData"
    :permissions="permissions"
    :endPoint="endPoint"
    :mode="addListMode"
    @close="showAddListForm = false"
    @save="handleListAdded"
    v-if="showAddListForm"
    @add="handleListAddedWhileCreating"
  />
</template>
